<template>
  <div class="chat-layout">
    <div class="chat-container">
      <!-- 左侧好友列表 -->
      <div class="friends-panel">
        <div class="friends-header">
          <h3>我的好友</h3>
          <div class="connection-status" :class="{ connected: wsConnected }">
            {{ wsConnected ? '已连接' : '未连接' }}
          </div>
        </div>
        <div class="friends-search">
          <el-input 
            v-model="searchQuery" 
            placeholder="搜索好友..." 
            prefix-icon="Search"
          />
        </div>
        <div class="friends-list">
          <div 
            v-for="friend in filteredFriends" 
            :key="friend.id" 
            :class="['friend-item', { active: selectedFriend?.id === friend.id }]"
            @click="selectFriend(friend)"
          >
            <el-badge :value="friend.unreadMessageCount || 0" :hidden="!friend.unreadMessageCount || friend.unreadMessageCount === 0" class="item">
              <div class="friend-info">
                <el-avatar 
                  :size="40" 
                  :src="friend.avatar || 'https://cube.elemecdn.com/0/88/03b0d39583f48206768a7534e55bcpng.png'" 
                  class="friend-avatar"
                />
                <div class="friend-details">
                  <h4>{{ friend.name }}</h4>
                  <p class="last-message">{{ friend.lastMessageContent || '暂无消息' }}</p>
                </div>
                <div class="message-time">
                  {{ friend.lastMessageTime ? formatDate(friend.lastMessageTime) : '' }}
                </div>
              </div>
            </el-badge>
          </div>
        </div>
      </div>

      <!-- 右侧聊天区域 -->
      <div v-if="selectedFriend" class="chat-panel">
        <div class="chat-header">
          <div class="friend-info">
            <el-avatar 
              :size="40" 
              :src="selectedFriend.avatar || 'https://cube.elemecdn.com/0/88/03b0d39583f48206768a7534e55bcpng.png'" 
              class="friend-avatar"
            />
            <span class="username">{{ selectedFriend.name }}</span>
          </div>
          <div class="header-actions">
            <!-- <el-button @click="markAllUnreadAsRead" size="small" plain>全部标记为已读</el-button> -->
            <el-button @click="selectedFriend = null" icon="Back">返回好友列表</el-button>
          </div>
        </div>

        <div class="chat-messages" ref="messagesContainerRef" @scroll="handleScroll">
          <!-- 加载更多按钮 -->
          <div class="load-more-container" v-if="state.hasMore">
            <el-button 
              v-if="!state.loadingMore" 
              @click="loadMoreMessages" 
              :loading="state.loadingMore"
              class="load-more-btn"
            >
              {{ state.currentPage > 0 ? '加载更多...' : '点击加载历史消息' }}
            </el-button>
            <div v-else class="loading-tips">加载中...</div>
          </div>
          
          <!-- 消息列表 -->
          <div 
            v-for="(msg, index) in state.messages" 
            :key="msg.id || index"
            class="message-item"
            :class="{
              'my-message': currentUserInfo && msg.fromUserId === currentUserInfo.id,
              'other-message': currentUserInfo && msg.fromUserId !== currentUserInfo.id
            }"
          >
            <!-- 其他用户消息 -->
            <div 
              v-if="currentUserInfo && msg.fromUserId !== currentUserInfo.id" 
              class="message-bubble other-bubble"
            >
              <div class="sender-info">
                <el-avatar 
                  :size="32" 
                  :src="msg.fromUserAvatar || selectedFriend?.avatar || 'https://cube.elemecdn.com/0/88/03b0d39583f48206768a7534e55bcpng.png'"
                  class="sender-avatar"
                />
                <div class="sender-name">{{ msg.fromUserName || selectedFriend?.name || '未知用户' }}</div>
              </div>
              <div class="message-content-wrapper" @click="markSingleMessageAsRead(msg)">
                <div class="message-content">{{ msg.content }}</div>
                <div class="message-status">

                  <span class="message-time">{{ formatDate(msg.createTime) }}</span>
                  <span class="other-message-read-status">{{ msg.isRead ? '已读' : '未读' }}</span>
                </div>
              </div>
            </div>
            
            <!-- 我的消息 -->
            <div 
              v-else-if="currentUserInfo && msg.fromUserId === currentUserInfo.id"
              class="message-bubble my-bubble"
            >
              <div class="sender-info-my">
                <el-avatar 
                  :size="32" 
                  :src="currentUserInfo.avatar || 'https://cube.elemecdn.com/0/88/03b0d39583f48206768a7534e55bcpng.png'"
                  class="sender-avatar-my"
                />
                <div class="sender-name-my">{{ currentUserInfo.name || '我' }}</div>
              </div>
              <div class="message-content-wrapper">
                <div class="message-content">{{ msg.content }}</div>
                <div class="message-status">
                  <span class="message-time">{{ formatDate(msg.createTime) }}</span>
                  <span class="my-message-read-status">{{ msg.isRead ? '已读' : '未读' }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="chat-input-area">
          <el-input
            v-model="state.inputMessage"
            type="textarea"
            :rows="4"
            placeholder="请输入消息..."
            maxlength="500"
            show-word-limit
            @keydown.enter="handleSendWithKeydown"
          />
          <div class="send-btn-container">
            <el-button type="primary" @click="sendMessage" :disabled="!state.inputMessage.trim()">发送</el-button>
          </div>
        </div>
      </div>

      <!-- 未选择好友时的提示 -->
      <div v-else class="welcome-panel">
        <div class="welcome-content">
          <h3>选择好友开始聊天</h3>
          <p>从左侧好友列表中选择一位好友开始私信交流</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted, computed, nextTick, reactive, triggerRef, watch } from 'vue';
import { useStore } from 'vuex';
import { ElMessage, ElBadge, ElMessageBox } from 'element-plus';
import { 
  sendUserMessage, 
  getUserMessagePage, 
  markMessageAsRead,
  batchMarkMessageAsRead
} from '@/user/api/interaction';
import { getUserFriends } from '@/user/api/userApi';
import { formatPostDate } from '@/utils/date';
import GlobalChatWebSocketService, { ChatMessage, ReadStatusMessage } from '@/user/services/globalChatService'; // 使用全局服务

interface Friend {
  id: number;
  name: string;
  avatar: string;
  lastMessageContent: string;
  lastMessageTime:string;
  unreadMessageCount: number;
}

interface Message {
  id: number;
  fromUserId: number;
  fromUserName: string;
  fromUserAvatar: string;
  content: string;
  createTime: string;
  isRead: number; // 新增字段 0未读1已读
}

const store = useStore();
const messagesContainerRef = ref<HTMLDivElement|null>(null);
const searchQuery = ref('');
const wsConnected = ref(false);

// 添加本地消息ID映射，用于关联本地临时ID和服务器真实ID
const localToServerIdMap = new Map<number, number>(); // 本地ID -> 服务器ID

// 好友列表
const friendsList = ref<Friend[]>([]);
const selectedFriend = ref<Friend | null>(null);

// 消息状态
const state = reactive({
  inputMessage: '',
  messages: [] as Message[],
  loadingMore: false,
  currentPage: 0,
  hasMore: true
});

// 计算属性
const currentUserInfo = computed(() => {
  const info = store.state.userInfo;
  console.log('当前用户信息:', info);
  return info;
});
const filteredFriends = computed(() => {
  if (!searchQuery.value) {
    return friendsList.value;
  }
  return friendsList.value.filter(friend => 
    friend.name.toLowerCase().includes(searchQuery.value.toLowerCase())
  );
});

// 选择好友
const selectFriend = async (friend: Friend) => {
  const previousFriendId = selectedFriend.value?.id;
  selectedFriend.value = friend;
  
  // 如果切换了好友，且之前的已选好友存在，可以尝试刷新其未读数（可选优化）
  
  // 重置消息状态
  state.messages = [];
  state.currentPage = 0;
  state.hasMore = true;
  
  // 加载该好友的消息，首次加载时滚动到底部
  await loadMessagesForFriend(friend.id, true);
  
  // 标记该好友的所有未读消息为已读
  if (friend.id) {
    try {
      // 获取与该好友的所有未读消息
      const friendUnreadMessages = state.messages.filter(msg => 
        msg.fromUserId === friend.id && msg.isRead === 0
      );
      const unreadMessageIds = friendUnreadMessages.map(msg => msg.id);
      
      if (unreadMessageIds.length > 0) {
        // 批量标记为已读
        await batchMarkMessageAsRead(unreadMessageIds);
        // 更新本地状态
        friendUnreadMessages.forEach(msg => {
          msg.isRead = 1;
        });
        triggerRef(state);
      }
      
      // 重要：根据实际标记为已读的消息数来扣减好友列表中的未读消息数
      // 避免直接清零，而是扣减已读消息数
      const friendInList = friendsList.value.find(f => f.id === friend.id);
      if (friendInList) {
        // 确保未读数不会变成负数
        friendInList.unreadMessageCount = Math.max(0, friendInList.unreadMessageCount - unreadMessageIds.length);
      }
    } catch (error) {
      console.error('批量标记消息已读失败:', error);
    }
  }
  
  // 滚动到底部
  scrollToBottom();
};

// 监听消息容器滚动事件，实现无感加载
const handleScroll = async (event: Event) => {
  const container = event.target as HTMLElement;
  
  // 由于现在消息是按时间正序排列，最早的在上，最新的在下
  // 当接近顶部时，说明用户想查看更早的消息
  if (container.scrollTop <= 200 && state.hasMore && !state.loadingMore) {
    console.log('滚动到顶部，加载更多消息');
    
    // 记录当前滚动位置
    const prevScrollHeight = container.scrollHeight;
    const prevScrollTop = container.scrollTop;
    
    await loadMoreMessages();
    
    // 等待 DOM 更新后，调整滚动位置以保持视觉连续性
    await nextTick(() => {
      // 计算需要补偿的高度差，以保持滚动位置不变
      const heightDiff = container.scrollHeight - prevScrollHeight;
      container.scrollTop = prevScrollTop + heightDiff;
    });
  }
};

// 格式化时间
const formatDate = (dateStr: string) => {
  return formatPostDate(dateStr);
};

// 获取头像
const getAvatarByUserId = (userId: number) => {
  if (userId === selectedFriend.value?.id) {
    return selectedFriend.value.avatar;
  }
  if (userId === currentUserInfo.value?.id) {
    return currentUserInfo.value?.avatar;
  }
  return null;
};

// 发送消息
const sendMessage = async () => {
  if (!state.inputMessage.trim()) {
    ElMessage.warning('请输入消息内容');
    return;
  }

  if (!selectedFriend.value) {
    ElMessage.warning('请选择好友');
    return;
  }

  const messageContent = state.inputMessage;
  
  try {
    // 检查当前用户信息是否存在
    if (!currentUserInfo.value) {
      ElMessage.error('用户未登录，无法发送消息');
      return;
    }

    // 生成一个临时ID用于本地显示
    const tempId = Date.now();
    
    // 构建临时消息对象
    const tempMessage: Message = {
      id: tempId,
      fromUserId: currentUserInfo.value.id,
      fromUserName: currentUserInfo.value.name || '我',
      fromUserAvatar: currentUserInfo.value.avatar,
      content: messageContent,
      createTime: new Date().toISOString(),
      isRead: 0 // 发送的消息初始状态为未读
    };
    
    // 添加到本地消息列表
    state.messages.push(tempMessage);
    
    // 滚动到底部
    scrollToBottom();
    
    // 发送消息到服务器
    const response = await sendUserMessage({
      toUserId: selectedFriend.value.id,
      content: messageContent
    });

    if (response.code === 1) {
      // 服务器返回真实ID后，更新本地消息的ID
      // 根据后端返回格式，response.data 直接是 ID 数字
      const realMessageId = response.data;
      if (realMessageId) {
        // 更新ID映射
        localToServerIdMap.set(tempId, realMessageId);
        
        // 更新本地消息的真实ID
        const messageIndex = state.messages.findIndex(msg => msg.id === tempId);
        if (messageIndex !== -1) {
          state.messages[messageIndex].id = realMessageId;
          console.log(`消息ID从 ${tempId} 更新为 ${realMessageId}`);
        }
      }
      
      // 清空输入框
      state.inputMessage = '';
      
      ElMessage.success('消息发送成功');
    } else {
      // 如果发送失败，移除本地临时消息
      const messageIndex = state.messages.findIndex(msg => msg.id === tempId);
      if (messageIndex !== -1) {
        state.messages.splice(messageIndex, 1);
      }
      ElMessage.error(response.msg || '消息发送失败');
    }
  } catch (error) {
    console.error('发送消息失败:', error);
    // 如果发生异常，也尝试移除可能已添加的临时消息（虽然通常在try块内处理）
    // 这里简单处理，主要依靠catch捕获网络错误等
    ElMessage.error('发送消息失败，请检查网络连接');
  }
};

// 处理发送快捷键
const handleSendWithKeydown = (event: KeyboardEvent) => {
  if (event.shiftKey) return; // 如果按下Shift键，不触发发送
  event.preventDefault(); // 阻止换行
  sendMessage();
};

// 加载与指定好友的消息
const loadMessagesForFriend = async (userId: number, scrollToBottomOnFirstLoad = true) => {
  if (state.loadingMore) return;
  
  const isFirstLoad = state.currentPage === 0; // 判断是否是首次加载
  
  state.loadingMore = true;
  
  try {
    const params = {
      page: state.currentPage + 1,
      pageSize: 20,
      targetUserId: userId
    };

    const response = await getUserMessagePage(params);

    if (response.code === 1) {
      const newMessages = response.data?.records || [];
      
      // 将新获取的历史消息添加到现有消息前面
      state.messages.unshift(...newMessages);
      
      // 按时间戳对所有消息进行排序，确保时间正序（从早到晚）
      state.messages.sort((a, b) => new Date(a.createTime).getTime() - new Date(b.createTime).getTime());
      
      // 更新页码和是否有更多数据的标志
      state.currentPage++;
      state.hasMore = newMessages.length === params.pageSize;
    } else {
      ElMessage.error(response.msg || '获取消息失败');
    }
  } catch (error) {
    console.error('加载消息失败:', error);
    ElMessage.error('加载消息失败');
  } finally {
    state.loadingMore = false;
    // 等待DOM更新后再滚动
    await nextTick();
    
    // 首次加载时滚动到底部
    // 加载更多历史消息时，由 handleScroll 负责恢复滚动位置，此处不进行滚动操作以避免冲突
    if (isFirstLoad && scrollToBottomOnFirstLoad) {
      scrollToBottom();
    }
  }
};

// 加载更多消息
const loadMoreMessages = async () => {
  if (state.loadingMore || !selectedFriend.value) return;

  state.loadingMore = true;
  try {
    const response = await getUserMessagePage({
      page: state.currentPage + 1,
      pageSize: 20,
      targetUserId: selectedFriend.value.id
    });

    if (response.code === 1) {
      const newMessages = response.data.records.map((record: any) => ({
        id: record.id,
        fromUserId: record.fromUserId,
        fromUserName: record.fromUserName,
        fromUserAvatar: record.fromUserAvatar,
        content: record.content,
        createTime: record.createTime,
        isRead: record.isRead
      }));

      // 按时间顺序排序（最早的在前，最新的在后）
      newMessages.sort((a, b) => new Date(a.createTime).getTime() - new Date(b.createTime).getTime());

      // 将新获取的消息添加到现有消息列表前面
      state.messages = [...newMessages, ...state.messages];

      // 更新分页信息
      state.currentPage++;
      state.hasMore = response.data.records.length === 20;

      // 标记新加载的未读消息为已读
      const unreadMessages = newMessages.filter(msg => msg.isRead === 0);
      if (unreadMessages.length > 0) {
        // 提取未读消息ID
        const unreadMessageIds = unreadMessages.map(msg => msg.id);
        
        try {
          // 批量标记为已读
          await batchMarkMessageAsRead(unreadMessageIds);
          
          // 更新本地消息状态
          state.messages.forEach(msg => {
            if (unreadMessageIds.includes(msg.id)) {
              msg.isRead = 1;
            }
          });
          
          // 重要：更新好友列表中的未读消息数
          const friendInList = friendsList.value.find(f => f.id === selectedFriend.value!.id);
          if (friendInList) {
            friendInList.unreadMessageCount = Math.max(0, friendInList.unreadMessageCount - unreadMessageIds.length);
          }
          
          console.log(`批量标记 ${unreadMessageIds.length} 条历史消息为已读`);
        } catch (error) {
          console.error('批量标记历史消息为已读失败:', error);
        }
      }
    }
  } catch (error) {
    console.error('加载更多消息失败:', error);
    ElMessage.error('加载更多消息失败');
  } finally {
    state.loadingMore = false;
  }
};

// 滚动到底部
const scrollToBottom = () => {
  nextTick(() => {
    if (messagesContainerRef.value) {
      messagesContainerRef.value.scrollTop = messagesContainerRef.value.scrollHeight;
    }
  });
};

// 滚动到顶部
const scrollToTop = () => {
  nextTick(() => {
    if (messagesContainerRef.value) {
      messagesContainerRef.value.scrollTop = 0;
    }
  });
};

// 标记单条消息为已读
const markSingleMessageAsRead = async (message: Message) => {
  // 只处理接收的消息，且是未读状态
  if (message.fromUserId !== currentUserInfo.value?.id && message.isRead === 0) {
    try {
      await markMessageAsRead(message.id);
      // 更新本地消息状态
      message.isRead = 1;
      triggerRef(state);
      console.log(`消息 ${message.id} 标记为已读成功`);
    } catch (error) {
      console.error(`标记消息 ${message.id} 为已读失败:`, error);
      ElMessage.error('标记消息为已读失败');
    }
  }
};

// 批量标记消息为已读
const markBatchMessagesAsRead = async (messagesToMark: Message[]) => {
  if (!messagesToMark || messagesToMark.length === 0) {
    ElMessage.warning('没有需要标记的消息');
    return;
  }

  // 过滤出未读的接收消息
  const unreadMessages = messagesToMark.filter(
    msg => msg.fromUserId !== currentUserInfo.value?.id && msg.isRead === 0
  );

  if (unreadMessages.length === 0) {
    ElMessage.info('没有未读消息需要标记');
    return;
  }

  const messageIds = unreadMessages.map(msg => msg.id);

  try {
    await batchMarkMessageAsRead(messageIds);
    
    // 更新本地消息状态
    unreadMessages.forEach(msg => {
      msg.isRead = 1;
    });
    triggerRef(state);
    
    ElMessage.success(`成功标记 ${unreadMessages.length} 条消息为已读`);
  } catch (error) {
    console.error('批量标记消息为已读失败:', error);
    ElMessage.error('批量标记消息为已读失败');
  }
};

// 标记当前会话所有未读消息为已读
const markAllUnreadAsRead = async () => {
  if (!selectedFriend.value) {
    ElMessage.warning('请先选择好友');
    return;
  }

  // 获取当前会话中所有未读的接收消息
  const unreadMessages = state.messages.filter(
    msg => msg.fromUserId === selectedFriend.value?.id && msg.isRead === 0
  );

  if (unreadMessages.length === 0) {
    ElMessage.info('没有未读消息');
    return;
  }

  try {
    // 确认操作
    await ElMessageBox.confirm(
      `确定要将与 ${selectedFriend.value.name} 的 ${unreadMessages.length} 条消息标记为已读吗？`,
      '确认标记',
      {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }
    );

    const messageIds = unreadMessages.map(msg => msg.id);
    await batchMarkMessageAsRead(messageIds);

    // 更新本地消息状态
    unreadMessages.forEach(msg => {
      msg.isRead = 1;
    });
    triggerRef(state);

    ElMessage.success(`成功标记 ${unreadMessages.length} 条消息为已读`);
  } catch (error) {
    if (error !== 'cancel') {
      console.error('批量标记所有未读消息失败:', error);
      ElMessage.error('批量标记失败');
    }
  }
};

// 处理接收到的新消息
const handleNewMessage = async (message: ChatMessage) => {
  console.log('收到新消息:', message);
  
  // 查找发送者是否在好友列表中
  const friendIndex = friendsList.value.findIndex(f => f.id === message.fromUserId);
  const friend = friendIndex !== -1 ? friendsList.value[friendIndex] : null;

  // 更新好友列表中的最后消息信息和未读数
  if (friend) {
    friend.lastMessageContent = message.content;
    friend.lastMessageTime = message.createTime;
    
    // 如果当前没有选中该好友，或者该消息不是当前激活窗口发出的（即后台接收），增加未读数
    // 注意：通常自己发的消息不会通过WS推给自己作为"新消息"提醒，除非是多端同步.
    // 假设 message.fromUserId !== currentUserInfo.value.id
    if (selectedFriend.value?.id !== message.fromUserId) {
      friend.unreadMessageCount = (friend.unreadMessageCount || 0) + 1;
      
      // 可选：播放提示音或显示通知
    } else {
      // 如果当前正与该好友聊天，收到对方消息，标记为已读
      // 这里主要是确保UI上未读数归零
      friend.unreadMessageCount = 0;
    }
  } else {
    // 如果不在好友列表中（例如陌生人消息，视业务逻辑而定），可能需要重新加载好友列表或忽略
    console.warn('收到非好友列表用户的消息:', message.fromUserId);
  }

  // 如果是当前聊天对象发来的消息，添加到当前消息列表
  if (selectedFriend.value && message.fromUserId === selectedFriend.value.id) {
    // 如果WebSocket消息中的用户名或头像为空，尝试从好友列表中获取
    const newMessage: Message = {
      id: message.id,
      fromUserId: message.fromUserId,
      fromUserName: message.fromUserName || friend?.name || selectedFriend.value.name || '未知用户',
      fromUserAvatar: message.fromUserAvatar || friend?.avatar || selectedFriend.value.avatar || 'https://cube.elemecdn.com/0/88/03b0d39583f48206768a7534e55bcpng.png',
      content: message.content,
      createTime: message.createTime,
      isRead: 1 // 新收到的消息默认为未读
    };
    
    state.messages.push(newMessage);
    scrollToBottom();
    
    // 关键修改：标记这条具体的单条消息为已读
    try {
      await markMessageAsRead(newMessage.id);
      // 更新本地消息状态
      newMessage.isRead = 1;
      triggerRef(state);
      console.log(`实时收到的消息 ${newMessage.id} 已标记为已读`);
    } catch (error) {
      console.error(`标记实时消息 ${newMessage.id} 为已读失败:`, error);
    }
  }
};

// 处理消息已读状态更新
const handleReadStatusUpdate = (readStatus: ReadStatusMessage) => {
  console.log('收到消息已读状态更新:', readStatus);
  
  // 首先尝试直接查找服务器ID对应的消息
  let messageIndex = state.messages.findIndex(msg => msg.id === readStatus.messageId);
  
  // 如果没找到，检查是否是本地ID映射
  if (messageIndex === -1) {
    // 遍历本地ID映射，看是否有对应的服务器ID
    for (const [localId, serverId] of localToServerIdMap.entries()) {
      if (serverId === readStatus.messageId) {
        // 找到了对应的本地ID，查找消息
        messageIndex = state.messages.findIndex(msg => msg.id === localId);
        if (messageIndex !== -1) {
          break;
        }
      }
    }
  }
  
  if (messageIndex !== -1) {
    // 更新消息的已读状态
    state.messages[messageIndex].isRead = 1;
    console.log(`消息 ${readStatus.messageId} 状态更新为已读`);
    // 触发响应式更新
    triggerRef(state);
  } else {
    console.warn(`未找到ID为 ${readStatus.messageId} 的消息，可能已过期或不在此会话中`);
  }
};

// 处理批量消息已读状态更新
const handleBatchReadStatusUpdate = (batchReadStatus: any) => {
  console.log('收到批量消息已读状态更新:', batchReadStatus);
  
  // 根据日志，这里的数据结构可能已经是解析后的对象，不需要再访问.data
  // 所以我们直接使用传入的 batchReadStatus 作为数据
  const data = batchReadStatus;
  
  // 检查数据结构是否符合预期
  if (!data || !data.messages) {
    console.error('批量已读状态数据格式错误:', batchReadStatus);
    return;
  }
  
  // 遍历消息数组，逐条更新本地消息列表的isRead状态
  data.messages.forEach((message: { messageId: number, content: string }) => {
    let messageIndex = state.messages.findIndex(msg => msg.id === message.messageId);
    
    // 如果没找到，检查是否是本地ID映射
    if (messageIndex === -1) {
      // 遍历本地ID映射，看是否有对应的服务器ID
      for (const [localId, serverId] of localToServerIdMap.entries()) {
        if (serverId === message.messageId) {
          // 找到了对应的本地ID，查找消息
          messageIndex = state.messages.findIndex(msg => msg.id === localId);
          if (messageIndex !== -1) {
            break;
          }
        }
      }
    }
    
    if (messageIndex !== -1) {
      // 更新消息的已读状态
      state.messages[messageIndex].isRead = 1;
      console.log(`消息 ${message.messageId} 状态更新为已读`);
    } else {
      console.warn(`未找到ID为 ${message.messageId} 的消息，可能已过期或不在此会话中`);
    }
  });
  
  // 触发响应式更新
  triggerRef(state);
  
  console.log(`批量更新了 ${data.messages.length} 条消息的已读状态`);
};

// 监听好友选择变化
watch(selectedFriend, (newVal) => {
  if (newVal) {
    scrollToBottom();
  }
}, { immediate: true });

// 初始化WebSocket连接
const initWebSocket = () => {
  // 获取token并建立WebSocket连接
  const token = store.state.token || localStorage.getItem('token');
  if (token) {
    console.log('正在连接WebSocket...');
    // 实际建立WebSocket连接
    GlobalChatWebSocketService.connect(token);
    
    // 添加消息处理器
    GlobalChatWebSocketService.addMessageHandler(handleNewMessage);
    // 添加消息已读状态处理器
    GlobalChatWebSocketService.addReadStatusHandler(handleReadStatusUpdate);
    
    // 添加批量消息已读状态处理器
    GlobalChatWebSocketService.addBatchReadStatusHandler(handleBatchReadStatusUpdate);
    
    // 添加连接状态监听
    GlobalChatWebSocketService.addConnectionHandler((connected) => {
      wsConnected.value = connected;
      console.log('WebSocket连接状态变更:', connected ? '已连接' : '未连接');
    });
    
    // 初始同步状态
    wsConnected.value = GlobalChatWebSocketService.isConnected();
  } else {
    console.warn('未找到token，无法建立WebSocket连接');
  }
};

// 加载好友列表
const loadFriends = async () => {
  try {
    const params = {
      page: 1,
      pageSize: 100  // 获取所有好友
    };
    
    console.log('正在请求好友列表，参数:', params);
    const response = await getUserFriends(params);
    console.log('获取好友列表响应:', response);
    
    let friends = [];
    
    if (response && typeof response === 'object' && 'code' in response) {
      if (response.code === 1) {
        friends = response.data?.records || [];
        console.log('加载的好友数量:', friends.length);
      } else {
        // 如果后端返回错误信息，则显示具体错误
        ElMessage.error(response.msg || '获取好友列表失败');
        console.error('获取好友列表失败:', response.msg);
        loadMockFriends();
        return;
      }
    } else {
      // 响应可能是直接的数据，没有code包装
      friends = response?.records || response || [];
      console.log('直接加载好友列表，数量:', friends.length);
    }

    // 获取每个好友的未读消息数 (如果API支持批量获取最好，否则可能需要单独请求或依赖WS更新)
    // 这里假设 getUserFriends 返回的数据里可能不包含实时未读数，或者我们需要额外请求
    // 为了简化，先赋值基础信息，然后尝试获取未读数映射（如果有这样的API）
    // 如果没有批量获取未读数的API，可以在循环中调用 getUnreadMessageCount (不推荐，性能差)
    // 或者依赖 WebSocket 推送来更新未读数，初始设为0
    
    friendsList.value = friends.map((f: any) => ({
      ...f,
      unreadMessageCount: f.unreadMessageCount || 0 // 初始化为0，等待WS更新或单独接口
    }));

    // 可选：如果有一个获取所有未读数的接口，可以在这里调用并合并数据
    // try {
    //   const unreadRes = await getUnreadMessageCount();
    //   if(unreadRes.code === 1) {
    //      // 合并 unreadRes.data 到 friendsList
    //   }
    // } catch(e) {}

  } catch (error: any) {
    console.error('获取好友列表失败:', error);
    if (error.response) {
      console.error('响应错误:', error.response.status, error.response.data);
      ElMessage.error(`获取好友列表失败: ${error.response.status} ${error.response.statusText}`);
    } else if (error.request) {
      console.error('请求错误:', error.request);
      ElMessage.error('网络错误，请检查网络连接');
    } else {
      console.error('其他错误:', error.message);
      ElMessage.error('获取好友列表失败: ' + error.message);
    }
    
    // 使用模拟数据作为后备方案
    loadMockFriends();
  }
};

// 模拟好友数据的函数
const loadMockFriends = () => {
  console.warn('使用模拟好友数据');
  friendsList.value = [
    { id: 1, name: '张三', avatar: 'https://cube.elemecdn.com/0/88/03b0d39583f48206768a7534e55bcpng.png', lastMessageContent:'你好！', lastMessageTime: new Date().toISOString() ,unreadMessageCount: 0} ,
    { id: 2, name: '李四', avatar: 'https://cube.elemecdn.com/0/88/03b0d39583f48206768a7534e55bcpng.png', lastMessageContent:'你好！', lastMessageTime: new Date().toISOString() ,unreadMessageCount: 0} ,
    { id:3, name: '王五', avatar: 'https://cube.elemecdn.com/0/88/03b0d39583f48206768a7534e55bcpng.png', lastMessageContent:'你好！', lastMessageTime: new Date().toISOString() ,unreadMessageCount: 0}
  ];
};

// 初始化
onMounted(async () => {
  console.log('ChatView组件挂载，开始初始化');
  await loadFriends();
  console.log('好友列表加载完成，添加WebSocket处理器');
  initWebSocket();
});

// 页面卸载时清理
onUnmounted(() => {
  console.log('ChatView组件卸载，移除WebSocket处理器');
  GlobalChatWebSocketService.removeMessageHandler(handleNewMessage);
  GlobalChatWebSocketService.removeReadStatusHandler(handleReadStatusUpdate);
  // 不断开连接，因为其他组件也可能在使用
});
</script>

<style scoped>
.chat-layout {
  height: calc(100vh - 120px);
  display: flex;
  flex-direction: column;
  flex: 1;
  padding: 20px;
  background: linear-gradient(135deg, #f9f2eb 0%, #fefefe 100%);
}

.section-header {
  text-align: center;
  margin-bottom: 25px;
}

.section-header h2 {
  font-size: 28px;
  color: #5a3e36;
  margin-bottom: 10px;
  font-family: 'Quicksand', cursive;
}

.section-header p {
  color: #8b6d4b;
  font-size: 16px;
}

.chat-container {
  display: flex;
  height: 100%;
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 6px 24px rgba(90, 62, 54, 0.15);
  flex: 1;
}

.friends-panel {
  width: 300px;
  border-right: 1px solid #e9dcc8;
  display: flex;
  flex-direction: column;
  flex-shrink: 0; /* 防止被挤压 */
  background: linear-gradient(to bottom, #fffdf9 0%, #fefefe 100%);
}

.friends-header {
  padding: 20px;
  border-bottom: 1px solid #e9dcc8;
  background: linear-gradient(to right, #f9f2eb, #fefefe);
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.friends-header h3 {
  margin: 0;
  font-size: 18px;
  color: #8B7D6B;
  font-weight: 600;
}

.connection-status {
  font-size: 12px;
  padding: 4px 8px;
  border-radius: 10px;
  background: #f0ebe1;
  color: #999;
}

.connection-status.connected {
  background: #e8f4ff;
  color: #4da1ff;
}

.friends-search {
  padding: 15px;
  border-bottom: 1px solid #e9dcc8;
  background: #f9f2eb;
}

.friend-item {
  padding: 15px;
  cursor: pointer;
  border-bottom: 1px solid #f5f0e6;
  transition: all 0.3s ease;
  border-left: 3px solid transparent;
}

.friend-item:hover {
  background-color: #f9f2eb;
  transform: translateX(3px);
}

.friend-item.active {
  background-color: #e8f4ff;
  border-left: 3px solid #5a3e36;
}

.friend-info {
  display: flex;
  align-items: center;
}

.friend-details {
  flex: 1;
  margin-left: 10px;
  overflow: hidden;
}

.friend-details h4 {
  margin: 0 0 5px 0;
  font-size: 15px;
  color: #333;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-weight: 500;
}

.last-message {
  margin: 0;
  font-size: 13px;
  color: #999;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.message-time {
  font-size: 12px;
  white-space: nowrap;
  text-align: right;
  min-width: 50px;
  margin-left: 5px;
}

.other-message-read-status {
  flex: 1;
  text-align: left;
  font-size: 12px;
  white-space: nowrap;
  min-width: 50px;
  margin-left: 5px;
}

.my-message-read-status {
  flex: 1;
  text-align: right;
  font-size: 12px;
  white-space: nowrap;
  min-width: 50px;
  margin-left: 5px;
}

.chat-panel {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0; /* 允许flex item收缩至内容宽度以下 */
  background: linear-gradient(160deg, #fdfaf3 0%, #fffdf9 100%);
}

.welcome-panel {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(160deg, #fdfaf3 0%, #fffdf9 100%);
}

.welcome-content {
  text-align: center;
  color: #999;
}

.welcome-content h3 {
  margin-bottom: 10px;
  color: #8B7D6B;
  font-size: 24px;
}

.chat-header {
  padding: 15px 20px;
  border-bottom: 1px solid #e9dcc8;
  display: flex;
  align-items: center;
  background: linear-gradient(to right, #f9f2eb, #fefefe);
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.05);
}

.friend-info {
  display: flex;
  align-items: center;
  flex: 1;
}

.username {
  margin-left: 12px;
  font-weight: 600;
  color: #5a3e36;
  font-size: 16px;
}

.chat-messages {
  flex: 1;
  padding: 20px;
  overflow-y: auto;
  max-height: calc(100vh - 280px);
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.load-more-container {
  text-align: center;
  margin-bottom: 15px;
}

.load-more-btn {
  background-color: #f0ebe1;
  border-color: #d4c9b9;
  color: #8B7D6B;
  border-radius: 20px;
  padding: 8px 20px;
  font-size: 14px;
}

.load-more-btn:hover {
  background-color: #e6dfd3;
  border-color: #c9bfa7;
}

.loading-more, .no-more {
  color: #999;
  font-size: 14px;
  padding: 10px;
  text-align: center;
}

.message-item {
  display: flex;
  margin-bottom: 10px;
  width: 100%;
}

.other-message {
  justify-content: flex-start;
}

.my-message {
  justify-content: flex-end;
}

.message-bubble {
  display: flex;
  align-items: flex-start; /* 使头像和消息内容顶部对齐 */
  gap: 10px;
  max-width: 80%;
}

.other-bubble {
  flex-direction: row;
}

.my-bubble {
  flex-direction: row-reverse;
}

.message-content-wrapper {
  display: flex;
  flex-direction: column;
  max-width: 70%; /* 限制消息气泡最大宽度 */
  align-items: flex-start; /* 默认左对齐 */
  gap: 4px; /* 消息内容和时间戳之间的间距 */
}

/* 为其他用户消息内容添加样式，确保时间戳左对齐 */
.other-bubble .message-content-wrapper {
  align-items: flex-start; /* 使对方消息内容左对齐 */
}

/* 为我的消息内容添加样式，确保时间戳正确对齐 */
.my-bubble .message-content-wrapper {
  align-items: flex-end; /* 使我的消息内容右对齐 */
}

.message-content {
  padding: 14px 18px;
  border-radius: 18px;
  word-wrap: break-word; /* 关键：允许长单词换行 */
  word-break: break-word; /* 在任意字符间换行 */
  overflow-wrap: break-word; /* 确保超长内容换行 */
  max-width: 100%;
  position: relative;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
}

.my-bubble .message-content {
  background: linear-gradient(135deg, #4da1ff 0%, #3a7dcc 100%);
  border-bottom-right-radius: 4px;
  color: white;
  margin-left: auto;
  /* 防止内容撑开 */
  overflow-wrap: break-word;
  word-wrap: break-word;
}

.other-bubble .message-content {
  background: white;
  border: 1px solid #e9dcc8;
  border-bottom-left-radius: 4px;
  color: #333;
  margin-right: auto;
  /* 防止内容撑开 */
  overflow-wrap: break-word;
  word-wrap: break-word;
}

.sender-info {
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 50px;
}

.sender-name {
  font-size: 12px;
  color: #666;
  margin-top: 4px;
  text-align: center;
  max-width: 100%;
  word-break: break-all;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.sender-name-my {
  font-size: 12px;
  color: #666;
  text-align: center;
  max-width: 100%;
  word-break: break-all;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  order: 2; /* 将用户名放在头像后面 */
}

.sender-avatar-my {
  order: 1; /* 将头像放在用户名前面 */
}

.message-time {
  font-size: 11px;
  color: #999; /* 默认为灰色，适用于对方消息 */
  text-align: right;
  margin-top: 4px;
}

/* 消息状态显示 */
.message-status {
  display: flex;
  justify-content: space-between;
  font-size: 11px;
  color: #999;
  margin-top: 4px;
}



.my-bubble .message-status {
  flex-direction: row-reverse;
}

.my-bubble .message-read-status {
  text-align: right;
}

.other-bubble .message-read-status {
  text-align: left;
}

.chat-input-area {
  padding: 20px;
  border-top: 1px solid #e9dcc8;
  background: linear-gradient(to bottom, #f9f2eb, #fefefe);
  box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.05);
}

.send-btn-container {
  margin-top: 15px;
  display: flex;
  justify-content: flex-end;
}

.send-btn-container .el-button {
  border-radius: 25px;
  padding: 12px 36px;
  background: linear-gradient(135deg, #8B7D6B 0%, #5a3e36 100%); /* 使用棕色系替代蓝色 */
  border: none;
  font-weight: 500;
  font-size: 14px;
  box-shadow: 0 4px 12px rgba(139, 125, 107, 0.3); /* 使用棕色系阴影 */
  transition: all 0.3s ease;
}

.send-btn-container .el-button:hover:not(:disabled) {
  background: linear-gradient(135deg, #9c8d7c 0%, #6d4e41 100%); /* 使用稍浅的棕色系 */
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(139, 125, 107, 0.4); /* 棕色系阴影 */
}

.send-btn-container .el-button:active:not(:disabled) {
  transform: translateY(0);
}

.send-btn-container .el-button:disabled {
  background: #cccccc;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

/* 美化输入框样式 */
:deep(.el-textarea__inner) {
  border: 1px solid #e9dcc8;
  border-radius: 12px;
  padding: 16px;
  font-size: 15px;
  line-height: 1.6;
  resize: vertical;
  transition: border-color 0.3s ease, box-shadow 0.3s ease;
  background-color: #fdfaf6;
  box-shadow: inset 0 2px 8px rgba(0, 0, 0, 0.05);
}

:deep(.el-textarea__inner):focus {
  border-color: #8B7D6B; /* 使用主题棕色替代蓝色 */
  box-shadow: 0 0 0 3px rgba(139, 125, 107, 0.2); /* 使用棕色系的外发光阴影 */
  outline: none;
}

/* 字数统计样式优化 */
:deep(.el-input__count) {
  color: #8B7D6B;
  font-size: 13px;
  background: transparent;
  margin-top: 5px;
}

.item {
  width: 100%;
}

/* 滚动条样式 */
.chat-messages::-webkit-scrollbar {
  width: 8px;
}

.chat-messages::-webkit-scrollbar-track {
  background: #f9f2eb;
}

.chat-messages::-webkit-scrollbar-thumb {
  background: #d4c9b9;
  border-radius: 4px;
}

.chat-messages::-webkit-scrollbar-thumb:hover {
  background: #c9bfa7;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .chat-layout {
    padding: 10px;
    height: calc(100vh - 80px);
  }

  .chat-container {
    flex-direction: column;
    border-radius: 8px;
  }

  .friends-panel {
    width: 100%;
    height: 40vh;
    min-height: 300px;
  }

  .chat-panel, .welcome-panel {
    height: calc(60vh - 60px);
    min-height: 300px;
  }

  .chat-messages {
    max-height: calc(60vh - 140px);
    padding: 15px;
  }

  .chat-header {
    padding: 12px 15px;
  }

  .username {
    font-size: 14px;
  }

  .chat-input-area {
    padding: 15px;
  }

  .friend-item {
    padding: 12px;
  }

  .friend-details h4 {
    font-size: 14px;
  }

  .last-message {
    font-size: 12px;
  }
}

@media (max-width: 480px) {
  .chat-layout {
    padding: 5px;
    height: calc(100vh - 60px);
  }

  .friends-panel {
    height: 45vh;
    min-height: 250px;
  }

  .chat-panel, .welcome-panel {
    height: calc(55vh - 50px);
    min-height: 200px;
  }

  .chat-messages {
    max-height: calc(55vh - 130px);
    padding: 10px;
  }

  .chat-header {
    padding: 10px;
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }

  .username {
    margin-left: 0;
    font-size: 16px;
  }

  .chat-input-area {
    padding: 10px;
  }

  .send-btn-container {
    margin-top: 8px;
  }

  .friend-info {
    align-items: center;
  }

  .friend-details {
    margin-left: 8px;
  }

  .friends-header {
    padding: 12px;
  }

  .friends-header h3 {
    font-size: 16px;
  }

  .friends-search {
    padding: 10px;
  }
}
</style>